import React, { useState, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import {
  Users, Search, MoreHorizontal, Mail, Edit, Shield, Crown,
  Code, Eye, UserCheck, Building, ChevronDown, ChevronUp, Plus, Trash2, User,
  Clock, AlertCircle, RefreshCw, Link as LinkIcon, Copy, Check
} from 'lucide-react'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { useProfiles, useUpdateProfile, useDeleteProfile, useProductionClients, type ProfileMember } from '@/hooks/useProfiles'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/integrations/supabase/client'
import { invokeTeamInvitation } from '@/lib/team-invitation'
import { toast } from 'sonner'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'

// --- Role display config ---

const ROLE_CONFIG: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  owner: { label: 'Owner', icon: Crown, color: 'bg-amber-50 text-amber-700 border-amber-200' },
  admin: { label: 'Admin', icon: Shield, color: 'bg-blue-50 text-blue-700 border-blue-200' },
  developer: { label: 'Developer', icon: Code, color: 'bg-purple-50 text-purple-700 border-purple-200' },
  manager: { label: 'Manager', icon: Users, color: 'bg-green-50 text-green-700 border-green-200' },
  member: { label: 'Member', icon: User, color: 'bg-teal-50 text-teal-700 border-teal-200' },
  viewer: { label: 'Viewer', icon: Eye, color: 'bg-slate-50 text-slate-700 border-slate-200' },
  client: { label: 'Client', icon: Building, color: 'bg-orange-50 text-orange-700 border-orange-200' },
}

// --- Permission sections (HeyReach-style) ---

const PERMISSION_SECTIONS = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    permissions: [
      { key: 'view', label: 'Can view dashboard and analytics' },
    ],
  },
  {
    key: 'campaigns',
    label: 'Campaigns',
    permissions: [
      { key: 'view', label: 'Can view campaigns' },
      { key: 'manage', label: 'Can create and manage campaigns' },
    ],
  },
  {
    key: 'master_inbox',
    label: 'Master Inbox',
    permissions: [
      { key: 'view', label: 'Can view replies' },
      { key: 'reply', label: 'Can reply to messages' },
    ],
  },
  {
    key: 'mailboxes',
    label: 'Mailboxes & Domains',
    permissions: [
      { key: 'view', label: 'Can view mailboxes and domains' },
      { key: 'manage', label: 'Can manage mailbox settings' },
    ],
  },
  {
    key: 'linkedin',
    label: 'LinkedIn',
    permissions: [
      { key: 'view', label: 'Can view LinkedIn profiles' },
      { key: 'manage', label: 'Can manage LinkedIn settings' },
    ],
  },
  {
    key: 'reports',
    label: 'Reports & Export',
    permissions: [
      { key: 'view', label: 'Can view reports' },
      { key: 'export', label: 'Can export data' },
    ],
  },
]

function getInitials(name: string | null, email: string | null): string {
  if (name) {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }
  return email?.charAt(0).toUpperCase() || '?'
}

/** Determine invitation status: 'accepted', 'pending', or 'expired' (7-day expiry) */
function getInvitationStatus(member: ProfileMember): 'accepted' | 'pending' | 'expired' {
  if (member.invitation_accepted_at) return 'accepted'
  const createdAt = new Date(member.created_at)
  const now = new Date()
  const daysSinceCreated = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24)
  return daysSinceCreated > 7 ? 'expired' : 'pending'
}

// --- Edit Member Dialog ---

function EditMemberDialog({
  member,
  open,
  onOpenChange,
}: {
  member: ProfileMember
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { data: clients = [] } = useProductionClients()
  const updateProfile = useUpdateProfile()

  const [selectedClientIds, setSelectedClientIds] = useState<string[]>(member.client_ids)
  const [selectedRole, setSelectedRole] = useState(member.role)
  const [permissions, setPermissions] = useState<Record<string, Record<string, boolean>>>(
    member.permissions as Record<string, Record<string, boolean>> || {}
  )
  const [showPermissions, setShowPermissions] = useState(false)

  // Reset state when dialog opens with new member
  React.useEffect(() => {
    if (open) {
      setSelectedClientIds(member.client_ids)
      setSelectedRole(member.role)
      setPermissions(member.permissions as Record<string, Record<string, boolean>> || {})
      setShowPermissions(false)
    }
  }, [open, member.id])

  const toggleClient = (clientId: string) => {
    setSelectedClientIds(prev =>
      prev.includes(clientId) ? prev.filter(id => id !== clientId) : [...prev, clientId]
    )
  }

  const togglePermission = (section: string, perm: string) => {
    setPermissions(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [perm]: !prev[section]?.[perm],
      },
    }))
  }

  const handleSave = async () => {
    try {
      await updateProfile.mutateAsync({
        id: member.id,
        role: selectedRole,
        client_ids: selectedClientIds,
        // Keep client_id in sync — use first client_id for backward compatibility
        client_id: selectedRole === 'client' ? (selectedClientIds[0] || null) : null,
        permissions,
      })
      toast.success(`Updated ${member.full_name || member.email}`)
      onOpenChange(false)
    } catch (err: any) {
      toast.error(err.message || 'Failed to update member')
    }
  }

  const workspaceCount = selectedClientIds.length

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Member</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {/* Email (read-only) */}
          <div>
            <Label className="text-sm font-medium">Email</Label>
            <div className="mt-1 px-3 py-2 bg-muted rounded-md text-sm">{member.email}</div>
          </div>

          {/* Role */}
          <div>
            <Label className="text-sm font-medium">Role</Label>
            <Select value={selectedRole} onValueChange={(v) => setSelectedRole(v as ProfileMember['role'])}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="owner">Owner</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="developer">Developer</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
                <SelectItem value="client">Client</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Workspaces */}
          <div>
            <Label className="text-sm font-medium">
              Workspaces ({workspaceCount} selected)
            </Label>
            <div className="mt-2 border rounded-lg divide-y max-h-48 overflow-y-auto">
              {clients.map(client => (
                <label
                  key={client.id}
                  className="flex items-center gap-3 px-3 py-2.5 hover:bg-muted/50 cursor-pointer"
                >
                  <Checkbox
                    checked={selectedClientIds.includes(client.id)}
                    onCheckedChange={() => toggleClient(client.id)}
                  />
                  <span className="text-sm">{client.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Permissions */}
          <div>
            <button
              onClick={() => setShowPermissions(!showPermissions)}
              className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground"
            >
              {showPermissions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              Permissions
            </button>

            {showPermissions && (
              <div className="mt-3 space-y-4">
                {PERMISSION_SECTIONS.map(section => (
                  <div key={section.key} className="border rounded-lg p-3">
                    <div className="text-sm font-medium mb-2">{section.label}</div>
                    <div className="space-y-2">
                      {section.permissions.map(perm => (
                        <label key={perm.key} className="flex items-center gap-2 cursor-pointer">
                          <Switch
                            checked={permissions[section.key]?.[perm.key] || false}
                            onCheckedChange={() => togglePermission(section.key, perm.key)}
                          />
                          <span className="text-sm text-muted-foreground">{perm.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Button onClick={handleSave} className="w-full" disabled={updateProfile.isPending}>
            {updateProfile.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// --- Invite Member Dialog ---

function InviteMemberDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const queryClient = useQueryClient()
  const { data: clients = [] } = useProductionClients()
  const activeClients = useMemo(
    () => clients.filter(c => c.status === 'Active'),
    [clients],
  )
  // One row per invitee. Role and workspaces are shared across the batch, which
  // is the common case: adding several people from the same client at once.
  const [invitees, setInvitees] = useState<Array<{ email: string; fullName: string }>>([
    { email: '', fullName: '' },
  ])
  const [role, setRole] = useState<ProfileMember['role']>('client')
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([])
  const [sending, setSending] = useState(false)
  const [skipEmail, setSkipEmail] = useState(false)
  const [generatedLinks, setGeneratedLinks] = useState<Array<{ email: string; link: string }>>([])
  const [copiedEmail, setCopiedEmail] = useState<string | null>(null)

  const toggleClient = (clientId: string) => {
    setSelectedClientIds(prev =>
      prev.includes(clientId) ? prev.filter(id => id !== clientId) : [...prev, clientId]
    )
  }

  const updateInvitee = (index: number, field: 'email' | 'fullName', value: string) => {
    setInvitees(prev => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)))
  }

  const addInvitee = () => setInvitees(prev => [...prev, { email: '', fullName: '' }])

  const removeInvitee = (index: number) =>
    setInvitees(prev => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)))

  const resetForm = () => {
    setInvitees([{ email: '', fullName: '' }])
    setRole('client')
    setSelectedClientIds([])
    setSkipEmail(false)
    setGeneratedLinks([])
    setCopiedEmail(null)
  }

  const handleDialogOpenChange = (next: boolean) => {
    if (!next) resetForm()
    onOpenChange(next)
  }

  const handleCopyLink = async (email: string, link: string) => {
    try {
      await navigator.clipboard.writeText(link)
      setCopiedEmail(email)
      toast.success('Invite link copied')
      setTimeout(() => setCopiedEmail(null), 2000)
    } catch {
      toast.error('Failed to copy — select the link and copy manually')
    }
  }

  const handleInvite = async () => {
    const rows = invitees
      .map(r => ({ email: r.email.trim().toLowerCase(), fullName: r.fullName.trim() }))
      .filter(r => r.email)

    if (rows.length === 0) {
      toast.error('At least one email is required')
      return
    }

    const seen = new Set<string>()
    const duplicate = rows.find(r => (seen.has(r.email) ? true : (seen.add(r.email), false)))
    if (duplicate) {
      toast.error(`${duplicate.email} is listed more than once`)
      return
    }

    setSending(true)
    setGeneratedLinks([])

    const loginUrl = `${window.location.origin}/auth`
    const clientNames = activeClients
      .filter(c => selectedClientIds.includes(c.id))
      .map(c => c.name)

    const links: Array<{ email: string; link: string }> = []
    const failures: Array<{ email: string; message: string }> = []

    // Sequential rather than parallel: the edge function creates auth users, and
    // one bad address should not take the rest of the batch down with it.
    for (const row of rows) {
      try {
        const data = await invokeTeamInvitation({
          memberName: row.fullName || row.email,
          memberEmail: row.email,
          clientName: clientNames.join(', ') || 'OutboundBuddy',
          inviterName: 'Admin',
          loginUrl,
          createUser: true,
          role,
          clientId: role === 'client' ? (selectedClientIds[0] || null) : null,
          clientIds: selectedClientIds,
          permissions: {},
          skipEmail,
        })

        if (skipEmail && data?.inviteLink) links.push({ email: row.email, link: data.inviteLink })
      } catch (err: any) {
        failures.push({ email: row.email, message: err?.message || 'Failed to send invitation' })
      }
    }

    setSending(false)
    queryClient.invalidateQueries({ queryKey: ['profiles'] })

    const succeeded = rows.length - failures.length

    if (failures.length) {
      toast.error(
        failures.length === rows.length
          ? (rows.length === 1 ? failures[0].message : `None sent. ${failures[0].message}`)
          : `${succeeded} of ${rows.length} invited. Failed: ${failures.map(f => f.email).join(', ')}`
      )
    }

    if (skipEmail) {
      setGeneratedLinks(links)
      if (!failures.length) {
        if (links.length) {
          toast.success(`${links.length} invite${links.length > 1 ? 's' : ''} created — copy below`)
        } else {
          toast.error('Invites created but no links were returned — use "Get invite link" from the row menu')
          handleDialogOpenChange(false)
        }
      }
      // Dialog stays open so the links can be copied.
      return
    }

    if (!failures.length) {
      toast.success(
        succeeded === 1 ? `Invitation sent to ${rows[0].email}` : `${succeeded} invitations sent`
      )
      handleDialogOpenChange(false)
    }
  }

  const filledCount = invitees.filter(r => r.email.trim()).length

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Invite Member</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <Checkbox
              checked={skipEmail}
              onCheckedChange={v => setSkipEmail(!!v)}
            />
            <span>Do not send invitation in email</span>
          </label>

          <div className="space-y-3">
            {invitees.map((row, index) => (
              <div key={index} className="space-y-2">
                {invitees.length > 1 && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">
                      Person {index + 1}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-muted-foreground hover:text-destructive"
                      onClick={() => removeInvitee(index)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
                <div>
                  <Label>Email</Label>
                  <Input
                    className="mt-1"
                    placeholder="Enter email address"
                    type="email"
                    value={row.email}
                    onChange={e => updateInvitee(index, 'email', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Full Name</Label>
                  <Input
                    className="mt-1"
                    placeholder="Enter full name"
                    value={row.fullName}
                    onChange={e => updateInvitee(index, 'fullName', e.target.value)}
                  />
                </div>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full"
              onClick={addInvitee}
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              Add another
            </Button>
          </div>

          <div>
            <Label>Role</Label>
            <Select value={role} onValueChange={(v) => setRole(v as ProfileMember['role'])}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="developer">Developer</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
                <SelectItem value="client">Client</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Workspaces</Label>
            <div className="mt-2 border rounded-lg divide-y max-h-48 overflow-y-auto">
              {activeClients.map(client => (
                <label
                  key={client.id}
                  className="flex items-center gap-3 px-3 py-2.5 hover:bg-muted/50 cursor-pointer"
                >
                  <Checkbox
                    checked={selectedClientIds.includes(client.id)}
                    onCheckedChange={() => toggleClient(client.id)}
                  />
                  <span className="text-sm">{client.name}</span>
                </label>
              ))}
            </div>
            {filledCount > 1 && (
              <p className="mt-1.5 text-xs text-muted-foreground">
                Role and workspaces apply to all {filledCount} people.
              </p>
            )}
          </div>

          {generatedLinks.length > 0 && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-blue-900">
                <LinkIcon className="h-4 w-4" />
                Invite link{generatedLinks.length > 1 ? 's' : ''} (no email was sent)
              </div>
              {generatedLinks.map(item => (
                <div key={item.email} className="space-y-1">
                  {generatedLinks.length > 1 && (
                    <div className="text-xs font-medium text-blue-900">{item.email}</div>
                  )}
                  <div className="flex items-stretch gap-2">
                    <Input
                      readOnly
                      value={item.link}
                      className="font-mono text-xs bg-white"
                      onFocus={e => e.currentTarget.select()}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="shrink-0"
                      onClick={() => handleCopyLink(item.email, item.link)}
                    >
                      {copiedEmail === item.email
                        ? <Check className="h-3.5 w-3.5 mr-1" />
                        : <Copy className="h-3.5 w-3.5 mr-1" />}
                      {copiedEmail === item.email ? 'Copied' : 'Copy'}
                    </Button>
                  </div>
                </div>
              ))}
              <p className="text-xs text-blue-800">
                These links expire shortly. Share them with the invitees through your preferred
                channel. They can request a fresh link from the row menu later.
              </p>
            </div>
          )}

          <Button onClick={handleInvite} className="w-full" disabled={sending}>
            {sending
              ? (skipEmail ? 'Creating...' : 'Sending...')
              : (skipEmail
                  ? `Create Invite${filledCount > 1 ? 's' : ''} & Show Link${filledCount > 1 ? 's' : ''}`
                  : `Send Invitation${filledCount > 1 ? 's' : ''}`)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// --- Get Invite Link Dialog (regenerate fresh link for pending invites) ---

function GetInviteLinkDialog({
  member,
  open,
  onOpenChange,
}: {
  member: ProfileMember
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [link, setLink] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [copied, setCopied] = useState(false)

  const fetchLink = async () => {
    if (!member.email) {
      toast.error('Member has no email — cannot generate a link')
      return
    }
    setLoading(true)
    setLink(null)
    try {
      const loginUrl = `${window.location.origin}/auth`
      const data = await invokeTeamInvitation({
        memberEmail: member.email,
        loginUrl,
        regenerateLinkOnly: true,
        // Required-for-validation placeholders ignored by the regenerateLinkOnly path
        memberName: member.full_name || member.email,
        clientName: 'OutboundBuddy',
        inviterName: 'Admin',
      })
      if (!data?.inviteLink) throw new Error('No link returned')
      setLink(data.inviteLink)
    } catch (err: any) {
      toast.error(err.message || 'Failed to generate link')
    } finally {
      setLoading(false)
    }
  }

  React.useEffect(() => {
    if (open) {
      setLink(null)
      setCopied(false)
      void fetchLink()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, member.id])

  const handleCopy = async () => {
    if (!link) return
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      toast.success('Invite link copied')
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Failed to copy — select the link and copy manually')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Invite link for {member.full_name || member.email}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {loading && (
            <div className="text-sm text-muted-foreground">Generating fresh link...</div>
          )}

          {!loading && link && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 space-y-2">
              <div className="flex items-stretch gap-2">
                <Input
                  readOnly
                  value={link}
                  className="font-mono text-xs bg-white"
                  onFocus={e => e.currentTarget.select()}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  onClick={handleCopy}
                >
                  {copied ? <Check className="h-3.5 w-3.5 mr-1" /> : <Copy className="h-3.5 w-3.5 mr-1" />}
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <p className="text-xs text-blue-800">
                Recovery links expire after a short time. If this one is rejected, click "Regenerate" to get a fresh one.
              </p>
            </div>
          )}

          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={fetchLink}
              disabled={loading}
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
              {loading ? 'Generating...' : 'Regenerate'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// --- Main Section ---

export function TeamMembersSection() {
  const queryClient = useQueryClient()
  const { data: profiles = [], isLoading } = useProfiles()
  const { data: clients = [] } = useProductionClients()
  const { profile: currentProfile, isAdmin, isOwner } = useAuth()
  const deleteProfile = useDeleteProfile()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'accepted' | 'pending' | 'expired'>('all')
  // 'all' | 'outboundbuddy' (internal staff: role !== 'client') | <client.id>
  const [clientFilter, setClientFilter] = useState<string>('all')
  const [editingMember, setEditingMember] = useState<ProfileMember | null>(null)
  const [deletingMember, setDeletingMember] = useState<ProfileMember | null>(null)
  const [linkMember, setLinkMember] = useState<ProfileMember | null>(null)
  const [showInviteDialog, setShowInviteDialog] = useState(false)

  // Realtime subscription: patch the React Query cache directly on profile
  // INSERT / UPDATE / DELETE so admins see status, role, and workspace changes
  // live (typically <1s) without a refetch. Subscription is scoped to this
  // component so only admins viewing the Team page hold an open channel.
  React.useEffect(() => {
    const normalize = (row: any): ProfileMember => ({
      ...row,
      client_ids: row.client_ids || [],
      permissions: row.permissions || {},
      invitation_accepted_at: row.invitation_accepted_at || null,
    })

    // Make sure the realtime client has the current user's JWT — required for
    // RLS-gated postgres_changes events. Without this, admin sessions silently
    // receive zero events because the realtime server can't evaluate RLS.
    let cancelled = false
    const setupAndSubscribe = async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession()
        const token = sessionData.session?.access_token
        if (token) {
          // setAuth is idempotent and the SDK normally syncs it from auth state,
          // but some auth flows leave the realtime socket stale — set it
          // explicitly here so we know the token is current at subscribe time.
          supabase.realtime.setAuth(token)
        }
      } catch (err) {
        console.warn('[team realtime] failed to refresh auth token:', err)
      }

      if (cancelled) return null

      const channel = supabase
        .channel('team-members-profiles')
        .on(
          'postgres_changes' as any,
          { event: '*', schema: 'public', table: 'profiles' },
          (payload: any) => {
            console.debug('[team realtime] event:', payload.eventType, payload.new || payload.old)
            queryClient.setQueryData<ProfileMember[]>(['profiles'], (prev) => {
              const list = prev ?? []
              if (payload.eventType === 'INSERT') {
                const next = normalize(payload.new)
                if (list.some(p => p.id === next.id)) return list
                return [...list, next]
              }
              if (payload.eventType === 'UPDATE') {
                const next = normalize(payload.new)
                return list.map(p => (p.id === next.id ? next : p))
              }
              if (payload.eventType === 'DELETE') {
                const oldId = (payload.old as any)?.id
                if (!oldId) return list
                return list.filter(p => p.id !== oldId)
              }
              return list
            })
          },
        )
        .subscribe((status, err) => {
          console.debug('[team realtime] subscription status:', status, err || '')
          if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
            console.warn('[team realtime] channel issue:', status, err)
          }
        })

      return channel
    }

    const channelPromise = setupAndSubscribe()

    return () => {
      cancelled = true
      void channelPromise.then((ch) => {
        if (ch) supabase.removeChannel(ch)
      })
    }
  }, [queryClient])

  const canDelete = isAdmin || isOwner

  // Map client IDs to names for display
  const clientNameMap = useMemo(() => {
    const map = new Map<string, string>()
    clients.forEach(c => map.set(c.id, c.name))
    return map
  }, [clients])

  const filteredProfiles = useMemo(() => {
    let filtered = profiles
    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => getInvitationStatus(p) === statusFilter)
    }
    if (clientFilter !== 'all') {
      if (clientFilter === 'outboundbuddy') {
        filtered = filtered.filter(p => p.role !== 'client')
      } else {
        filtered = filtered.filter(p =>
          p.client_id === clientFilter || p.client_ids?.includes(clientFilter)
        )
      }
    }
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter(p =>
        p.full_name?.toLowerCase().includes(search) ||
        p.email?.toLowerCase().includes(search) ||
        p.role.toLowerCase().includes(search)
      )
    }
    return filtered
  }, [profiles, searchTerm, statusFilter, clientFilter])

  const getWorkspaceNames = (member: ProfileMember): string[] => {
    return member.client_ids
      .map(id => clientNameMap.get(id))
      .filter((name): name is string => !!name)
  }

  const stats = useMemo(() => {
    const admins = profiles.filter(p => ['owner', 'admin', 'developer'].includes(p.role)).length
    const clientUsers = profiles.filter(p => p.role === 'client').length
    const pending = profiles.filter(p => getInvitationStatus(p) !== 'accepted').length
    return { total: profiles.length, admins, clientUsers, pending }
  }, [profiles])

  if (isLoading) {
    return <div className="flex items-center justify-center py-12 text-muted-foreground">Loading members...</div>
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className={`grid gap-4 ${stats.pending > 0 ? 'grid-cols-4' : 'grid-cols-3'}`}>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{stats.total}</div>
            <div className="text-sm text-muted-foreground">Total Members</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{stats.admins}</div>
            <div className="text-sm text-muted-foreground">Admin / Internal</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{stats.clientUsers}</div>
            <div className="text-sm text-muted-foreground">Client Users</div>
          </CardContent>
        </Card>
        {stats.pending > 0 && (
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
              <div className="text-sm text-muted-foreground">Pending Invitations</div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Members Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Users className="h-5 w-5" />
            Members
          </CardTitle>
          {canDelete && (
            <Button onClick={() => setShowInviteDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Invite Member
            </Button>
          )}
        </CardHeader>

        <CardContent>
          {/* Search & Filter */}
          <div className="flex gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search members..."
                className="pl-9"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filter by workspace" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Workspaces</SelectItem>
                <SelectItem value="outboundbuddy">Outbound Buddy (Internal)</SelectItem>
                {clients
                  .filter(c => c.status === 'Active')
                  .map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Members</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr className="text-left text-sm text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Member</th>
                  <th className="px-4 py-3 font-medium">Role</th>
                  <th className="px-4 py-3 font-medium">Workspaces</th>
                  <th className="px-4 py-3 font-medium">Created</th>
                  <th className="px-4 py-3 font-medium w-12"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredProfiles.map(member => {
                  const roleConfig = ROLE_CONFIG[member.role] || ROLE_CONFIG.viewer
                  const RoleIcon = roleConfig.icon
                  const workspaces = getWorkspaceNames(member)
                  const inviteStatus = getInvitationStatus(member)

                  return (
                    <tr key={member.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="text-xs bg-primary/10">
                              {getInitials(member.full_name, member.email)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="text-sm font-medium">
                              {member.full_name || '-'}
                            </div>
                            <div className="text-xs text-muted-foreground">{member.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {inviteStatus === 'accepted' ? (
                            <Badge variant="outline" className={`${roleConfig.color} gap-1`}>
                              <RoleIcon className="h-3 w-3" />
                              {roleConfig.label}
                            </Badge>
                          ) : inviteStatus === 'pending' ? (
                            <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 gap-1">
                              <Clock className="h-3 w-3" />
                              Pending Invitation
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200 gap-1">
                              <AlertCircle className="h-3 w-3" />
                              Expired Invitation
                            </Badge>
                          )}
                          {inviteStatus !== 'accepted' && canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-muted-foreground hover:text-foreground"
                              title="Resend invitation"
                              onClick={async () => {
                                try {
                                  const loginUrl = `${window.location.origin}/auth`
                                  const clientNames = getWorkspaceNames(member)
                                  await invokeTeamInvitation({
                                    memberName: member.full_name || member.email,
                                    memberEmail: member.email,
                                    clientName: clientNames.join(', ') || 'OutboundBuddy',
                                    inviterName: 'Admin',
                                    loginUrl,
                                    createUser: true,
                                    role: member.role,
                                    clientId: member.client_id,
                                    clientIds: member.client_ids,
                                  })
                                  toast.success(`Invitation resent to ${member.email}`)
                                } catch (err: any) {
                                  toast.error(err?.message || 'Failed to resend invitation')
                                }
                              }}
                            >
                              <RefreshCw className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {['owner', 'admin', 'developer'].includes(member.role) && workspaces.length === 0 ? (
                          <span className="text-xs text-muted-foreground">All Workspaces</span>
                        ) : workspaces.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {workspaces.slice(0, 3).map(name => (
                              <Badge key={name} variant="secondary" className="text-xs font-normal">
                                {name}
                              </Badge>
                            ))}
                            {workspaces.length > 3 && (
                              <Badge variant="secondary" className="text-xs font-normal">
                                +{workspaces.length - 3} more
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">No workspaces</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {new Date(member.created_at).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric'
                        })}
                      </td>
                      <td className="px-4 py-3">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {canDelete && (
                              <DropdownMenuItem onClick={() => setEditingMember(member)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Edit Member
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={async () => {
                                try {
                                  const loginUrl = `${window.location.origin}/auth`
                                  const clientNames = getWorkspaceNames(member)
                                  await invokeTeamInvitation({
                                    memberName: member.full_name || member.email,
                                    memberEmail: member.email,
                                    clientName: clientNames.join(', ') || 'OutboundBuddy',
                                    inviterName: 'Admin',
                                    loginUrl,
                                    createUser: true,
                                    role: member.role,
                                    clientId: member.client_id,
                                    clientIds: member.client_ids,
                                  })
                                  toast.success(`Invitation resent to ${member.email}`)
                                } catch (err: any) {
                                  toast.error(err?.message || 'Failed to resend invitation')
                                }
                              }}
                            >
                              <Mail className="h-4 w-4 mr-2" />
                              Resend Invitation
                            </DropdownMenuItem>
                            {inviteStatus !== 'accepted' && (
                              <DropdownMenuItem onClick={() => setLinkMember(member)}>
                                <LinkIcon className="h-4 w-4 mr-2" />
                                Get Invite Link
                              </DropdownMenuItem>
                            )}
                            {canDelete && member.id !== currentProfile?.id && member.role !== 'owner' && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  onClick={() => setDeletingMember(member)}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Remove Member
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            {filteredProfiles.length === 0 && (
              <div className="text-center py-8 text-muted-foreground text-sm">
                {searchTerm ? 'No members match your search' : 'No members found'}
              </div>
            )}
          </div>

          <div className="mt-3 text-xs text-muted-foreground text-right">
            Showing {filteredProfiles.length} of {profiles.length} members
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      {editingMember && (
        <EditMemberDialog
          member={editingMember}
          open={!!editingMember}
          onOpenChange={(open) => { if (!open) setEditingMember(null) }}
        />
      )}

      {/* Invite Dialog */}
      <InviteMemberDialog
        open={showInviteDialog}
        onOpenChange={setShowInviteDialog}
      />

      {/* Get Invite Link Dialog */}
      {linkMember && (
        <GetInviteLinkDialog
          member={linkMember}
          open={!!linkMember}
          onOpenChange={(open) => { if (!open) setLinkMember(null) }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletingMember} onOpenChange={(open) => { if (!open) setDeletingMember(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{deletingMember?.full_name || deletingMember?.email}</strong>?
              They will lose access to the platform immediately. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                if (!deletingMember) return
                try {
                  await deleteProfile.mutateAsync(deletingMember.id)
                  toast.success(`${deletingMember.full_name || deletingMember.email} has been removed`)
                  setDeletingMember(null)
                } catch (err: any) {
                  toast.error(err.message || 'Failed to remove member')
                }
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
